/**
 * Centralized Error Logger for Technician App.
 *
 * Captures ALL frontend errors and sends them to the backend database.
 * When offline, errors are stored in IndexedDB and flushed on reconnect.
 *
 * What is captured:
 *   - Unhandled JS errors (window.onerror)
 *   - Unhandled promise rejections (unhandledrejection)
 *   - API call failures (axios interceptor)
 *   - Sync queue processing errors
 *   - Vue component errors (app.config.errorHandler)
 *   - Manual logError() calls from catch blocks
 *
 * Each log entry includes:
 *   - user_id, user_email, domain_id (who)
 *   - error_type, message, stack_trace (what)
 *   - url, component, action (where)
 *   - job_id, job_number (context)
 *   - is_online, pending_queue_size (connectivity state)
 *   - user_agent, app_version, timestamp
 */
import db from './offlineDb'
import { isTechPreview } from '../preview/techPreview'

const MAX_BUFFER_SIZE = 500
const FLUSH_INTERVAL = 60000 // flush every 60s when online
const MAX_STACK_LENGTH = 4000
const MAX_MESSAGE_LENGTH = 1000
// A flaky mobile uplink fires the SAME connectivity error many times in a few
// seconds (heartbeat + GPS + sync poll + page GETs all fail at once). Collapse
// identical network_error/timeout entries within this window so they don't flood
// tech_error_logs / the hourly digest. Real app errors are NEVER deduped.
const NETWORK_DEDUP_WINDOW = 15000
const _recentNetworkLogs = new Map() // dedupKey -> last timestamp (ms)

function _shouldDedupNetwork(errorType, key) {
  if (errorType !== 'network_error' && errorType !== 'timeout') return false
  const now = Date.now()
  if (_recentNetworkLogs.size > 200) {
    for (const [k, t] of _recentNetworkLogs) {
      if (now - t > NETWORK_DEDUP_WINDOW) _recentNetworkLogs.delete(k)
    }
  }
  const last = _recentNetworkLogs.get(key)
  if (last && (now - last) < NETWORK_DEDUP_WINDOW) return true
  _recentNetworkLogs.set(key, now)
  return false
}

/**
 * Expected API outcomes that the UI already handles (retry / confirm dialog).
 * Must stay quiet in tech_error_logs and the hourly digest — otherwise dual-tech
 * autosave races and intentional "confirm remove active tech" flows look like
 * crashes (TICK-00040 digest: 409 on /technician/jobs/internal_*, 400 on /jobs/*).
 *
 * Accepts either an axios error or a log context with api_status / api_response.
 */
export function isHandledApiNoise(error, context = {}) {
  const status = context.api_status ?? error?.response?.status
  const body = context.api_response ?? error?.response?.data
  if (!status || body == null) return false

  let parsed = body
  if (typeof body === 'string') {
    try { parsed = JSON.parse(body) } catch { parsed = { raw: body } }
  }
  const code = parsed?.error ?? parsed?.code
  const codeVal = Array.isArray(code) ? code[0] : code
  const raw = typeof body === 'string' ? body : ''

  // Optimistic concurrency: caller re-reads version and retries.
  if (status === 409 && (codeVal === 'version_conflict' || raw.includes('version_conflict'))) {
    return true
  }
  // Office job edit: UI opens a confirmation dialog for tech_change_reason.
  if (status === 400 && (codeVal === 'active_tech_removal' || raw.includes('active_tech_removal'))) {
    return true
  }
  return false
}

let _apiClient = null
let _flushTimer = null
let _getUserInfo = null
let _started = false

function _now() {
  return new Date().toISOString()
}

function _truncate(str, max) {
  if (!str) return ''
  return typeof str === 'string' ? str.slice(0, max) : String(str).slice(0, max)
}

function _safeStringify(obj) {
  try {
    return JSON.stringify(obj, null, 0).slice(0, 2000)
  } catch {
    return '[unserializable]'
  }
}

function _extractMessage(error) {
  if (!error) return 'Unknown error'
  if (typeof error === 'string') return error

  // Standard Error objects
  if (error.message && typeof error.message === 'string') return error.message

  // Axios error: response.data might have detail/error/message
  const rd = error.response?.data
  if (rd) {
    if (typeof rd === 'string') return rd
    if (rd.detail) return String(rd.detail)
    if (rd.error) return String(rd.error)
    if (rd.message) return String(rd.message)
    return _safeStringify(rd).slice(0, 500)
  }

  // Axios code/status
  if (error.code) return `${error.code} ${error.response?.status || ''} ${error.config?.url || ''}`.trim()

  // Plain object fallback
  try {
    const s = JSON.stringify(error)
    if (s && s !== '{}') return s.slice(0, 500)
  } catch { /* ignore */ }

  return String(error)
}

function _getConnectivityState() {
  try {
    const isOnline = navigator.onLine
    return { is_online: isOnline }
  } catch {
    return { is_online: true }
  }
}

function _getPageInfo() {
  try {
    return {
      url: window.location.pathname + window.location.hash,
      user_agent: navigator.userAgent?.slice(0, 300) || '',
    }
  } catch {
    return { url: '', user_agent: '' }
  }
}

function _getUserData() {
  if (_getUserInfo) {
    try {
      return _getUserInfo()
    } catch { /* ignore */ }
  }
  // Fallback: read from localStorage
  try {
    const raw = localStorage.getItem('user')
    if (raw) {
      const u = JSON.parse(raw)
      return {
        user_id: u.id || null,
        user_email: u.email || '',
        domain_id: u.current_domain_id || null,
      }
    }
  } catch { /* ignore */ }
  return { user_id: null, user_email: '', domain_id: null }
}

// Browser-extension noise. Extensions (e.g. "Urban VPN Proxy") inject content
// scripts that hook XMLHttpRequest/fetch and throw inside their OWN code
// (chrome-extension:// / moz-extension:// ...). Those exceptions bubble up as
// window errors / unhandled rejections on OUR page even though they are not our
// bug, and a single user with such an extension can generate tens of thousands
// of rows (observed: one user, ~25k "reading 'M_ID'" rejections). We drop an
// error only when its THROW SITE (first stack frame) — or the reporting file —
// lives in an extension, so genuine app errors that merely pass through an
// extension frame lower down are still recorded. No look-behind (iOS 15 safe).
const EXTENSION_URL_RE = /(?:chrome|chrome-untrusted|moz|safari-web|safari|ms-browser|extension)-extension:\/\//i

function _isBrowserExtensionError(stack, component) {
  try {
    if (component && EXTENSION_URL_RE.test(component)) return true
    if (!stack) return false
    const lines = String(stack).split('\n').map((l) => l.trim()).filter(Boolean)
    // First frame = throw site. Chrome: "at fn (chrome-extension://...)".
    // Firefox: "fn@moz-extension://...". Match either shape.
    const firstFrame = lines.find((l) => l.startsWith('at ') || l.includes('@'))
    if (firstFrame && EXTENSION_URL_RE.test(firstFrame)) return true
    // Fallback: stack references an extension and has NO app (http[s]) frame,
    // i.e. it is entirely third-party injected code.
    if (EXTENSION_URL_RE.test(stack) && !/https?:\/\//.test(stack)) return true
  } catch { /* never throw from the logger */ }
  return false
}

/**
 * Write a single error log entry to IndexedDB buffer.
 */
async function _bufferLog(entry) {
  try {
    const count = await db.errorLogs.where('synced').equals(0).count()
    if (count >= MAX_BUFFER_SIZE) {
      // Drop oldest unsynced entries to prevent storage bloat
      const oldest = await db.errorLogs.where('synced').equals(0).limit(50).sortBy('createdAt')
      if (oldest.length > 0) {
        await db.errorLogs.bulkDelete(oldest.map(e => e.id))
      }
    }
    await db.errorLogs.add(entry)
  } catch (e) {
    // IndexedDB is broken/unavailable (e.g. the iOS WebKit Blob bug, private
    // mode, quota). Don't silently lose the error — best-effort send it
    // straight to the backend so critical failures are still recorded.
    console.error('[ErrorLogger] IndexedDB buffer failed, sending directly:', e)
    try {
      if (_apiClient) {
        _apiClient.post('/fsa/error-logs/', { logs: [entry] }).catch(() => {})
      }
    } catch (_) { /* never throw from the logger */ }
  }
}

/**
 * Log an error. Can be called from anywhere in the app.
 *
 * @param {string} errorType   Classification: 'js_error' | 'api_error' | 'vue_error' | 'sync_error' | 'network_error' | 'custom'
 * @param {string|Error} error The error object or message
 * @param {Object} context     Optional extra context { component, action, job_id, job_number, extra }
 */
export async function logError(errorType, error, context = {}) {
  // Skip offline rejections — they are expected, not errors
  if (error?.__offline) return

  // Handled conflicts / confirm-gated validation (see isHandledApiNoise).
  // Covers axios interceptor AND direct callers (offlineApi, store catch).
  if (isHandledApiNoise(error, context)) return

  // Tech App Preview simulator: don't persist anything — IndexedDB is SHARED
  // with the admin tab (only local/sessionStorage are shimmed), and read-only
  // tokens 403 the flush POST anyway. Console output is enough for debugging.
  if (isTechPreview()) {
    console.error(`[ErrorLog:${errorType}] (preview, not persisted)`, _extractMessage(error), context)
    return
  }

  const msg = _extractMessage(error)
  const stack = error?.stack || ''

  // Drop third-party browser-extension noise (see _isBrowserExtensionError).
  if (_isBrowserExtensionError(stack, context.component)) return

  // Collapse bursts of identical connectivity failures (network_error/timeout)
  // from the same endpoint within a short window — see NETWORK_DEDUP_WINDOW.
  if (_shouldDedupNetwork(errorType, `${errorType}|${context.api_url || ''}|${msg}`)) return

  const user = _getUserData()
  const page = _getPageInfo()
  const conn = _getConnectivityState()

  const entry = {
    synced: 0,
    createdAt: _now(),
    error_type: errorType || 'unknown',
    message: _truncate(msg, MAX_MESSAGE_LENGTH),
    stack_trace: _truncate(stack, MAX_STACK_LENGTH),
    url: page.url,
    component: _truncate(context.component || '', 200),
    action: _truncate(context.action || '', 200),
    job_id: context.job_id || null,
    job_number: context.job_number || null,
    user_id: user.user_id,
    user_email: user.user_email,
    domain_id: user.domain_id,
    is_online: conn.is_online,
    user_agent: page.user_agent,
    app_version: (typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : ''),
    extra: context.extra ? _safeStringify(context.extra) : null,
    // API-specific fields
    api_url: _truncate(context.api_url || '', 500),
    api_method: context.api_method || '',
    api_status: context.api_status || null,
    api_response: context.api_response ? _truncate(_safeStringify(context.api_response), 2000) : null,
  }

  await _bufferLog(entry)

  // Also console.error for dev visibility
  console.error(`[ErrorLog:${errorType}]`, msg, context)
}

/**
 * Flush buffered error logs to the backend.
 * Called periodically and on network recovery.
 */
export async function flushErrorLogs() {
  if (!_apiClient) return
  // Preview simulator must never POST error logs (read-only 403 / wrong author).
  if (isTechPreview()) return

  let batch
  try {
    batch = await db.errorLogs.where('synced').equals(0).limit(50).toArray()
  } catch {
    return
  }

  if (batch.length === 0) return

  try {
    await _apiClient.post('/fsa/error-logs/', { logs: batch })
    // Mark as synced
    const ids = batch.map(e => e.id)
    await db.errorLogs.where('id').anyOf(ids).modify({ synced: 1 })
    console.warn(`[ErrorLogger] Flushed ${batch.length} error logs to server`)
  } catch (e) {
    // Server unreachable — will retry next cycle
    console.warn('[ErrorLogger] Flush failed, will retry:', e.message)
  }

  // Clean up old synced entries (keep last 24h only)
  try {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    await db.errorLogs.where('synced').equals(1).and(r => r.createdAt < cutoff).delete()
  } catch { /* ignore */ }
}

/**
 * Get count of unsynced error logs (for UI if needed).
 */
export async function getUnsyncedCount() {
  try {
    return await db.errorLogs.where('synced').equals(0).count()
  } catch {
    return 0
  }
}

/**
 * Initialize the error logger. Call once from main.js.
 *
 * @param {Object} apiClient  Axios instance
 * @param {Function} getUserInfo  Function returning { user_id, user_email, domain_id }
 */
export function initErrorLogger(apiClient, getUserInfo) {
  if (_started) return
  _started = true
  _apiClient = apiClient
  _getUserInfo = getUserInfo

  // 1. Global JS errors
  window.addEventListener('error', (event) => {
    const msg = event.message || ''
    if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return
    logError('js_error', event.error || event.message, {
      component: event.filename || '',
      action: `line ${event.lineno}:${event.colno}`,
    })
  })

  // 2. Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    // Skip offline rejections — they are expected
    if (error?.__offline) return

    // Skip canceled/aborted requests (debounce, route change, AbortController,
    // logout abortAllRequests). These are expected, not bugs — mirrors the
    // axios interceptor skip so we don't raise false "Request aborted" incidents.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') return

    // Skip chunk load errors — handled by auto-reload in index.html and router
    const msg = error?.message || String(error || '')
    if (
      msg === 'canceled' ||
      msg === 'Request aborted' ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('is not a valid JavaScript MIME type') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('Unable to preload CSS')
    ) return

    logError('promise_rejection', error, {
      action: 'unhandledrejection',
    })
  })

  // 3. Periodic flush (guarded by visibility — errors from idle/hidden tabs flush on resume)
  _flushTimer = setInterval(() => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) return
    flushErrorLogs()
  }, FLUSH_INTERVAL)

  // 4. Flush on network recovery
  window.addEventListener('online', () => {
    setTimeout(() => flushErrorLogs(), 3000) // small delay for connection stabilization
  })

  // 5. Flush on page unload (best effort)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && navigator.onLine) {
      flushErrorLogs()
    }
  })

  console.warn('[ErrorLogger] Initialized — all frontend errors will be logged to DB')
}

/**
 * Vue error handler — install on app instance.
 * Usage: app.config.errorHandler = vueErrorHandler
 */
export function vueErrorHandler(err, instance, info) {
  const componentName = instance?.$options?.name || instance?.$.type?.name || instance?.$.type?.__name || 'Unknown'
  // TEMP DEBUG: full stack trace to console for TDZ diagnostics
  try {
    console.error('[Vue ErrorHandler]', componentName, info, '\n', err?.stack || err?.message || err)
  } catch { /* noop */ }
  logError('vue_error', err, {
    component: componentName,
    action: info || '',
    extra: { props: instance?.$props },
  })
}

/**
 * Axios interceptor to log API errors.
 * Usage: apiClient.interceptors.response.use(null, axiosErrorInterceptor)
 */
export function axiosErrorInterceptor(error) {
  // Skip offline rejections
  if (error?.__offline) return Promise.reject(error)
  // Skip canceled/aborted requests (debounce, navigation, AbortController)
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') return Promise.reject(error)
  // Also catch axios cancel with just message (older axios versions / manual cancel tokens)
  if (error?.message === 'canceled' || error?.message === 'Request aborted') return Promise.reject(error)
  // Skip 401s (normal auth flow)
  if (error?.response?.status === 401) return Promise.reject(error)

  // Skip errors from internal/expected endpoints to prevent noise
  const reqUrl = error?.config?.url || ''
  const httpStatus = error?.response?.status
  // Login form validation rejections come back as 400 (empty/invalid phone or
  // password, wrong app/account state). These are expected user-input failures,
  // not bugs — the UI already shows the message. Only skip 400 here; real bad
  // password is 401 (already skipped above).
  if (httpStatus === 400 && (reqUrl.includes('/auth/login') || reqUrl.includes('/auth/technician-login'))) {
    return Promise.reject(error)
  }
  if (reqUrl.includes('/fsa/error-logs') || reqUrl.includes('/fsa/tech-events')) {
    return Promise.reject(error)
  }
  // SIP call data 404 = race condition (SIP server not ready yet), not a bug
  if (reqUrl.includes('/dialer/sip-call-data') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // Twilio call event 404 = call already handled by another tab, expected for multi-tab users
  if (reqUrl.includes('/dialer/twilio-call-event') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // Start recording 400/500 with "not eligible" = call state doesn't allow recording, transient
  if (reqUrl.includes('/dialer/start-recording') && (httpStatus === 400 || httpStatus === 500)) {
    return Promise.reject(error)
  }
  // Job drafts 404 = draft already submitted/converted, expected
  if (reqUrl.includes('/jobs/drafts/') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // Branding endpoints 401/403 = user has no branding permission, normal for techs/clients
  if (reqUrl.includes('/branding/') && (httpStatus === 401 || httpStatus === 403 || httpStatus === 404)) {
    return Promise.reject(error)
  }
  if (reqUrl.includes('/account/my-theme') && (httpStatus === 401 || httpStatus === 403 || httpStatus === 404)) {
    return Promise.reject(error)
  }
  // Dialer toggle-accepting 404 = SIP session expired, transient
  if (reqUrl.includes('/dialer/toggle-accepting') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // SIP twilio hold/transfer 404 = call already ended
  if (reqUrl.includes('twilio/hold') && httpStatus === 404) {
    return Promise.reject(error)
  }
  if (reqUrl.includes('twilio/transfer') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // AI analysis 404 = call has no analysis (missed call / voicemail / pending), not an error
  if (reqUrl.includes('/crm/ai-analysis/') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // Voice robot 404 = domain has no active robot configured (e.g. new tenant),
  // the assistant falls back to defaults — expected state, not an error
  if (reqUrl.includes('/support/voice-robots/active') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // AI coach assignments 404 = no assignments for this call, not an error
  if (reqUrl.includes('/sales/ai-assignments') && httpStatus === 404) {
    return Promise.reject(error)
  }
  // Handled conflicts / confirm-gated validation — see isHandledApiNoise.
  if (isHandledApiNoise(error)) {
    return Promise.reject(error)
  }

  const config = error?.config || {}
  // Opt-out: best-effort / glanceable requests can mark themselves so an
  // expected failure (e.g. a 404 for a stale job) does not spam the error log.
  if (config.skipErrorLog) {
    return Promise.reject(error)
  }
  const responseData = error?.response?.data
  const httpStatus2 = error?.response?.status

  // Classify so connectivity/server failures are no longer silently dropped —
  // they get logged (for non-noise endpoints) under a dedicated type so they
  // are findable and analysable, without being confused with app bugs.
  let errType = 'api_error'
  if (error?.code === 'ECONNABORTED' || (typeof error?.message === 'string' && error.message.toLowerCase().includes('timeout'))) {
    errType = 'timeout'
  } else if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || httpStatus2 === 502 || httpStatus2 === 503) {
    errType = 'network_error'
  }

  logError(errType, error, {
    api_url: config.url || '',
    api_method: (config.method || '').toUpperCase(),
    api_status: httpStatus2 || 0,
    api_response: responseData,
    action: `${(config.method || '').toUpperCase()} ${config.url || ''}`,
  })

  return Promise.reject(error)
}
